import React, { useState, useEffect } from 'react';
import './ConditionEditor.css';

const ConditionEditor = ({ condition = {}, devices = [], onSave, onCancel }) => {
  const [type, setType] = useState(condition.type || 'device');
  
  // Device condition fields
  const [deviceId, setDeviceId] = useState(condition.deviceId || '');
  const [attribute, setAttribute] = useState(condition.attribute || '');
  const [deviceOperator, setDeviceOperator] = useState(condition.operator || '==');
  const [deviceValue, setDeviceValue] = useState(condition.value || '');
  const [parentId, setParentId] = useState(condition.parentId || '');
  
  // Time condition fields
  const [timeOperator, setTimeOperator] = useState(condition.operator || 'between');
  const [timeStart, setTimeStart] = useState(condition.start || '08:00');
  const [timeEnd, setTimeEnd] = useState(condition.end || '17:00');
  const [timeValue, setTimeValue] = useState(condition.value || '08:00');
  
  // Day condition fields
  const [dayOperator, setDayOperator] = useState(condition.operator || 'in');
  const [dayValue, setDayValue] = useState(condition.value || []);
  const [selectedDay, setSelectedDay] = useState('');

  // Get device attributes when deviceId changes
  const [deviceAttributes, setDeviceAttributes] = useState([]);
  
  useEffect(() => {
    if (deviceId) {
      // Find the selected device
      const device = devices.find(d => d.id === deviceId);
      
      if (device) {
        // Extract available attributes from device
        const attributes = [];
        
        // Add all properties from controls
        if (device.controls) {
          device.controls.forEach(control => {
            if (control.property && !attributes.includes(control.property)) {
              attributes.push(control.property);
            }
          });
        }
        
        // Add all properties from sensors
        if (device.sensors) {
          device.sensors.forEach(sensor => {
            if (sensor.property && !attributes.includes(sensor.property)) {
              attributes.push(sensor.property);
            }
          });
        }
        
        setDeviceAttributes(attributes);
        
        // If current attribute is not in the list, reset it
        if (attribute && !attributes.includes(attribute)) {
          setAttribute('');
        }
        
        // Check if this is a child device
        if (device.parentId && device.parentId !== deviceId) {
          setParentId(device.parentId);
        } else {
          setParentId('');
        }
      }
    }
  }, [deviceId, devices, attribute]);

  const handleSave = () => {
    let newCondition = { type };
    
    if (type === 'device') {
      newCondition = {
        ...newCondition,
        deviceId,
        attribute,
        operator: deviceOperator,
        value: deviceValue
      };
      
      // Only include parentId if it exists
      if (parentId) {
        newCondition.parentId = parentId;
      }
    } else if (type === 'time') {
      if (timeOperator === 'between') {
        newCondition = {
          ...newCondition,
          operator: timeOperator,
          start: timeStart,
          end: timeEnd
        };
      } else {
        newCondition = {
          ...newCondition,
          operator: timeOperator,
          value: timeValue
        };
      }
    } else if (type === 'day') {
      newCondition = {
        ...newCondition,
        operator: dayOperator,
        value: dayOperator === 'in' ? [...dayValue] : selectedDay
      };
    }
    
    onSave(newCondition);
  };

  const handleAddDay = () => {
    if (selectedDay && !dayValue.includes(selectedDay)) {
      setDayValue([...dayValue, selectedDay]);
      setSelectedDay('');
    }
  };

  const handleRemoveDay = (day) => {
    setDayValue(dayValue.filter(d => d !== day));
  };

  return (
    <div className="condition-editor">
      <div className="form-group">
        <label htmlFor="condition-type">Condition Type</label>
        <select
          id="condition-type"
          value={type}
          onChange={(e) => setType(e.target.value)}
        >
          <option value="device">Device State</option>
          <option value="time">Time</option>
          <option value="day">Day of Week</option>
        </select>
      </div>
      
      {type === 'device' && (
        <>
          <div className="form-group">
            <label htmlFor="device-id">Device</label>
            <select
              id="device-id"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              required
            >
              <option value="">Select a device</option>
              {devices.map(device => (
                <option key={device.id} value={device.id}>
                  {`${device.name} (${device.location || 'N/A'}, Floor ${device.floor || 'N/A'})`}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="attribute">Attribute</label>
            <select
              id="attribute"
              value={attribute}
              onChange={(e) => setAttribute(e.target.value)}
              required
              disabled={!deviceId}
            >
              <option value="">Select an attribute</option>
              {deviceAttributes.map(attr => (
                <option key={attr} value={attr}>
                  {attr}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="device-operator">Operator</label>
            <select
              id="device-operator"
              value={deviceOperator}
              onChange={(e) => setDeviceOperator(e.target.value)}
              required
            >
              <option value="==">Equals (==)</option>
              <option value="!=">Not Equals (!=)</option>
              <option value=">">Greater Than (&gt;)</option>
              <option value=">=">Greater Than or Equal (&gt;=)</option>
              <option value="<">Less Than (&lt;)</option>
              <option value="<=">Less Than or Equal (&lt;=)</option>
              <option value="contains">Contains</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="device-value">Value</label>
            <input
              id="device-value"
              type="text"
              value={deviceValue}
              onChange={(e) => setDeviceValue(e.target.value)}
              required
            />
          </div>
          
          {parentId && (
            <div className="form-group">
              <label>Parent Device</label>
              <div className="parent-device-info">
                {devices.find(d => d.id === parentId)?.name || parentId}
              </div>
            </div>
          )}
        </>
      )}
      
      {type === 'time' && (
        <>
          <div className="form-group">
            <label htmlFor="time-operator">Time Condition</label>
            <select
              id="time-operator"
              value={timeOperator}
              onChange={(e) => setTimeOperator(e.target.value)}
              required
            >
              <option value="between">Between Times</option>
              <option value="==">At Specific Time</option>
            </select>
          </div>
          
          {timeOperator === 'between' ? (
            <div className="form-row">
              <div className="form-group half">
                <label htmlFor="time-start">Start Time</label>
                <input
                  id="time-start"
                  type="time"
                  value={timeStart}
                  onChange={(e) => setTimeStart(e.target.value)}
                  required
                />
              </div>
              
              <div className="form-group half">
                <label htmlFor="time-end">End Time</label>
                <input
                  id="time-end"
                  type="time"
                  value={timeEnd}
                  onChange={(e) => setTimeEnd(e.target.value)}
                  required
                />
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="time-value">Time</label>
              <input
                id="time-value"
                type="time"
                value={timeValue}
                onChange={(e) => setTimeValue(e.target.value)}
                required
              />
            </div>
          )}
        </>
      )}
      
      {type === 'day' && (
        <>
          <div className="form-group">
            <label htmlFor="day-operator">Day Condition</label>
            <select
              id="day-operator"
              value={dayOperator}
              onChange={(e) => setDayOperator(e.target.value)}
              required
            >
              <option value="in">On Selected Days</option>
              <option value="==">On Specific Day</option>
            </select>
          </div>
          
          {dayOperator === 'in' ? (
            <>
              <div className="form-row">
                <div className="form-group flex-grow">
                  <label htmlFor="day-select">Add Day</label>
                  <select
                    id="day-select"
                    value={selectedDay}
                    onChange={(e) => setSelectedDay(e.target.value)}
                  >
                    <option value="">Select day</option>
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                    <option value="saturday">Saturday</option>
                    <option value="sunday">Sunday</option>
                  </select>
                </div>
                
                <button 
                  type="button" 
                  className="add-day-btn"
                  onClick={handleAddDay}
                  disabled={!selectedDay}
                >
                  Add
                </button>
              </div>
              
              <div className="selected-days">
                {dayValue.length === 0 ? (
                  <p className="no-days">No days selected</p>
                ) : (
                  dayValue.map(day => (
                    <div key={day} className="day-tag">
                      <span>{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                      <button 
                        type="button"
                        onClick={() => handleRemoveDay(day)}
                        className="remove-day-btn"
                      >
                        &times;
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="form-group">
              <label htmlFor="specific-day">Day</label>
              <select
                id="specific-day"
                value={selectedDay}
                onChange={(e) => setSelectedDay(e.target.value)}
                required
              >
                <option value="">Select day</option>
                <option value="monday">Monday</option>
                <option value="tuesday">Tuesday</option>
                <option value="wednesday">Wednesday</option>
                <option value="thursday">Thursday</option>
                <option value="friday">Friday</option>
                <option value="saturday">Saturday</option>
                <option value="sunday">Sunday</option>
              </select>
            </div>
          )}
        </>
      )}
      
      <div className="condition-editor-actions">
        <button 
          type="button" 
          className="cancel-btn"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button 
          type="button" 
          className="save-btn"
          onClick={handleSave}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default ConditionEditor;